import { Component, ErrorInfo, ReactNode, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Layout } from "@/components/layout";
import { trackEvent } from "@/lib/api";

import Home from "@/pages/Home";
import Momentos from "@/pages/Momentos";
import MomentoDetalle from "@/pages/MomentoDetalle";
import Servicios from "@/pages/Servicios";
import ServicioDetalle from "@/pages/ServicioDetalle";
import ComoTeAcompanamos from "@/pages/ComoTeAcompanamos";
import RutaLegal from "@/pages/RutaLegal";
import Orientador from "@/pages/Orientador";
import AtencionPrioritaria from "@/pages/AtencionPrioritaria";
import Nosotros from "@/pages/Nosotros";
import Guias from "@/pages/Guias";
import GuiaDetalle from "@/pages/GuiaDetalle";
import Contacto from "@/pages/Contacto";
import Privacidad from "@/pages/Privacidad";
import Terminos from "@/pages/Terminos";
import NoEncontrado from "@/pages/NoEncontrado";
import AbogadosTexcoco from "@/pages/AbogadosTexcoco";
import Estudio from "@/pages/Estudio";

class ErrorBoundary extends Component<{ children: ReactNode }, { error: boolean }> {
  state = { error: false };
  static getDerivedStateFromError() {
    return { error: true };
  }
  componentDidCatch(err: Error, info: ErrorInfo) {
    console.error(err, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-parchment px-6 text-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-azul mb-4">Algo salió mal</p>
            <h1 className="font-serif text-3xl text-ink mb-6">No pudimos mostrar esta página</h1>
            <a href="/" className="text-azul underline underline-offset-4">Volver al inicio</a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = (window as any).__lenis;
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    trackEvent("page_view", { path: pathname });
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    (window as any).__lenis = lenis;
    let raf = 0;
    const loop = (time: number) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      (window as any).__lenis = undefined;
    };
  }, []);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <SmoothScroll />
        <ScrollToTop />
        <Routes>
          <Route path="/estudio" element={<Estudio />} />
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/momentos" element={<Momentos />} />
            <Route path="/momentos/:slug" element={<MomentoDetalle />} />
            <Route path="/servicios" element={<Servicios />} />
            <Route path="/servicios/:slug" element={<ServicioDetalle />} />
            <Route path="/como-te-acompanamos" element={<ComoTeAcompanamos />} />
            <Route path="/ruta-legal" element={<RutaLegal />} />
            <Route path="/orientador" element={<Orientador />} />
            <Route path="/atencion-prioritaria" element={<AtencionPrioritaria />} />
            <Route path="/nosotros" element={<Nosotros />} />
            <Route path="/guias" element={<Guias />} />
            <Route path="/guias/:slug" element={<GuiaDetalle />} />
            <Route path="/contacto" element={<Contacto />} />
            <Route path="/privacidad" element={<Privacidad />} />
            <Route path="/terminos-de-uso" element={<Terminos />} />
            <Route path="/abogados-texcoco" element={<AbogadosTexcoco />} />
            <Route path="*" element={<NoEncontrado />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
